import React, { useEffect, useState, useCallback } from "react";
import Layout from "../components/Layout";
import { API, BASE_URL } from "../api";
import Pagination from "../components/Pagination";

import {
  FiTrash2,
  FiEdit,
  FiEye,
  FiPlus,
  FiSave,
  FiPackage,
  FiSearch,
} from "react-icons/fi";
import { MdGTranslate } from "react-icons/md"; // Material language icons
import { toasts } from "../components/AppToast";

// EMPTY FORM

const EMPTY_FORM = {
  lang_name: "",
  lang_flag: null,
};

// MANAGE LANGUAGE
export default function ManageLanguage() {
  // DATA
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  // SEARCH
  const [search, setSearch] = useState("");
  // PAGINATION
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  // FORM
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  // MODALS
  const [formModal, setFormModal] = useState(false);
  const [viewModal, setViewModal] = useState(null);
  const [delModal, setDelModal] = useState(null);
  const [delSaving, setDelSaving] = useState(false);
  // FETCH DATA
  const fetchData = useCallback(async () => {
    if (limit === null) {
      return;
    }

    setLoading(true);

    try {
      const response = await API.get("/languagespage", {
        params: {
          page,
          limit,
          search: search.trim(),
        },
      });

      const responseData = response.data;

      // console.log("LANGUAGE API RESPONSE:", responseData);

      // DATA

      const rows = Array.isArray(responseData?.data) ? responseData.data : [];

      // PAGINATION

      const pagination = responseData?.pagination || {};

      const totalCount = Number(pagination.total || 0);

      const currentPage = Number(pagination.page || page);

      const currentLimit = Number(pagination.limit || limit);

      const pages = Number(
        pagination.totalPages || Math.ceil(totalCount / currentLimit)
      );

      setData(rows);
      setTotal(totalCount);
      setTotalPages(pages);

      // Keep current page synchronized
      if (currentPage !== page) {
        setPage(currentPage);
      }
    } catch (error) {
      console.error(
        "FETCH LANGUAGE ERROR:",
        error.response?.data || error.message
      );

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to load languages"
      );

      setData([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search]);

  useEffect(() => {
    if (limit === null) {
      return;
    }

    fetchData();
  }, [page, limit, search, fetchData]);

  // SEARCH

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  // CLEAR SEARCH

  const clearSearch = () => {
    setSearch("");
    setPage(1);
  };

  // RESET FORM

  const resetForm = () => {
    setForm({
      ...EMPTY_FORM,
    });

    setEditId(null);
  };

  // OPEN ADD MODAL

  const openAddModal = () => {
    resetForm();
    setFormModal(true);
  };

  // CLOSE FORM MODAL

  const closeFormModal = () => {
    if (saving) {
      return;
    }

    setFormModal(false);
    resetForm();
  };

  // HANDLE FORM CHANGE

  const handleChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  // EDIT

  const startEdit = (row) => {
    setForm({
      lang_name: row.lang_name || "",
      lang_flag: null,
    });

    setEditId(row.lang_id);

    setFormModal(true);
  };

  // VIEW

  const handleView = (row) => {
    setViewModal(row);
  };

  // FLAG URL

  const getFlagUrl = (flag) => {
    if (!flag) {
      return null;
    }

    if (flag.startsWith("http://") || flag.startsWith("https://")) {
      return flag;
    }

    return `${BASE_URL.replace(/\/$/, "")}/${flag.replace(/^\//, "")}`;
  };

  // TOGGLE STATUS

  const handleStatusToggle = async (row) => {
    const newStatus = Number(row.lang_active) === 1 ? 0 : 1;

    try {
      await API.put(`/languages/status/${row.lang_id}`, {
        lang_active: newStatus,
      });

      toasts.success(
        newStatus === 1
          ? "Language activated successfully "
          : "Language deactivated successfully "
      );

      // Refresh current page
      await fetchData();
    } catch (error) {
      console.error(
        "Language status update error:",
        error.response?.data || error.message
      );

      toasts.error(
        error.response?.data?.message || "Failed to update language status"
      );
    }
  };

  // DEFAULT LANGUAGE

  const handleDefaultLanguage = async (row) => {
    try {
      await API.put(`/languages/default/${row.lang_id}/`);

      toasts.success(`${row.lang_name} set as default language successfully`);

      await fetchData();
    } catch (error) {
      console.error(
        "Default language update error:",
        error.response?.data || error.message
      );

      toasts.error(
        error.response?.data?.message || "Failed to update default language"
      );
    }
  };

  // SUBMIT ADD / EDIT

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.lang_name.trim()) {
      toasts.error("Language name is required");
      return;
    }

    try {
      setSaving(true);

      const formData = new FormData();

      formData.append("txtlanguage", form.lang_name.trim());

      if (form.lang_flag) {
        formData.append("txtImageUpload", form.lang_flag);
      }

      let response;

      // EDIT
      if (editId) {
        response = await API.put(`/languages/${editId}`, formData);
      }

      // ADD
      else {
        response = await API.post("/languages", formData);
      }

      if (response.data?.success) {
        toasts.success(
          response.data.message ||
            (editId
              ? "Language updated successfully"
              : "Language added successfully")
        );

        setForm({
          lang_name: "",
          lang_flag: null,
        });

        setEditId(null);
        setFormModal(false);

        await fetchData();
      }
    } catch (error) {
      console.error(
        editId ? "Update language error:" : "Add language error:",
        error.response?.data || error.message
      );

      toasts.error(error.response?.data?.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  // DELETE

  const handleDelete = async () => {
    if (!delModal) {
      return;
    }

    setDelSaving(true);

    try {
      await API.delete(`/languages/${delModal.lang_id}`);

      toasts.success("Language deleted successfully");

      setDelModal(null);

      // IF LAST RECORD ON CURRENT PAGE

      if (data.length === 1 && page > 1) {
        setPage((previous) => previous - 1);
      } else {
        await fetchData();
      }
    } catch (error) {
      console.error(
        "Delete Language error:",
        error.response?.data || error.message
      );

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Delete language failed"
      );
    } finally {
      setDelSaving(false);
    }
  };

  // LOADING PAGE LIMIT

  if (limit === null) {
    return (
      <Layout activePage="Language">
        <main className="dashboard-main">
          <div className="table-card">
            <div className="empty-table-cell text-center py-5">
              <div className="empty-icon">
                <FiPackage />
              </div>

              <div className="empty-title">Loading settings...</div>
            </div>
          </div>
        </main>
      </Layout>
    );
  }
  // =========================================================
  // PAGINATION - PREVIOUS
  // =========================================================

  const handlePrevious = () => {
    if (loading || page <= 1) {
      return;
    }

    setPage((prev) => Math.max(1, prev - 1));
  };

  // =========================================================
  // PAGINATION - NEXT
  // =========================================================

  const handleNext = () => {
    if (loading || totalPages <= 0 || page >= totalPages) {
      return;
    }

    setPage((prev) => Math.min(totalPages, prev + 1));
  };

  // RETURN

  return (
    <Layout activePage="Language">
      <main className="dashboard-main">
        {/*  TABLE CARD*/}

        <div className="table-card">
          {/* HEADER */}

          <div className="table-card-head">
            <h3 className="table-card-title d-flex align-items-center">
              <MdGTranslate size={26} className="me-2" />
              <span>Manage Language</span>
            </h3>

            {/*   SEARCH */}

            <div className="search-wrapper">
              <span className="search-icon">
                <FiSearch size={17} />
              </span>

              <input
                type="text"
                placeholder="Search Language..."
                value={search}
                onChange={handleSearch}
                className="search-input"
              />

              {search && (
                <button
                  type="button"
                  className="season-clear-btn"
                  onClick={clearSearch}
                >
                  ✕
                </button>
              )}
            </div>

            {/*   ADD */}

            <button
              type="button"
              className="um-btn-primary"
              onClick={openAddModal}
            >
              <span className="um-add-icon">＋</span>
              Add Language
            </button>
          </div>

          {/* TABLE */}

          <div className="table-responsive">
            <table className="um-table">
              <thead>
                <tr className="um-table-head">
                  <th className="um-th text-center">#</th>

                  <th className="um-th text-center">Language Name</th>

                  <th className="um-th text-center">Country Flag</th>

                  <th className="um-th text-center">Status</th>

                  <th className="um-th text-center">Default Language</th>

                  <th className="um-th text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {/*  LOADING */}

                {loading ? (
                  Array.from({
                    length: 5,
                  }).map((_, index) => (
                    <tr key={`loading-${index}`}>
                      {Array.from({
                        length: 6,
                      }).map((_, cellIndex) => (
                        <td
                          key={`skeleton-${cellIndex}`}
                          className="um-td text-center"
                        >
                          <div className="skeleton" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="empty-table-cell text-center py-5"
                    >
                      <div className="empty-icon">🌐</div>

                      <div className="empty-title">
                        {search
                          ? "No language records found"
                          : "No language records found"}
                      </div>

                      <div className="empty-sub">
                        {search
                          ? "Try a different search."
                          : "Use the Add Language button to add a language"}
                      </div>
                    </td>
                  </tr>
                ) : (
                  data.map((row, index) => {
                    const flagUrl = getFlagUrl(row.lang_flag);
                    const isActive = Number(row.lang_active) === 1;

                    return (
                      <tr
                        key={row.lang_id}
                        className={`um-row ${
                          index % 2 === 0 ? "even-row" : "odd-row"
                        }`}
                      >
                        {/* NUMBER */}

                        <td className="um-td text-center">
                          {(page - 1) * limit + index + 1}
                        </td>

                        {/* LANGUAGE NAME */}

                        <td className="um-td text-center">
                          <span className="car-name-badge">
                            <strong>{row.lang_name || "—"}</strong>
                          </span>
                        </td>

                        {/* FLAG */}

                        <td className="um-td text-center">
                          {flagUrl ? (
                            <img
                              src={flagUrl}
                              alt={row.lang_name || "Language flag"}
                              crossOrigin="anonymous"
                              style={{
                                width: "40px",
                                height: "28px",
                                objectFit: "cover",
                                borderRadius: "4px",
                                border: "1px solid #ddd",
                              }}
                            />
                          ) : (
                            "—"
                          )}
                        </td>

                        {/* STATUS */}

                        <td className="um-td">
                          <div className="status-wrap">
                            <div
                              className={`status-toggle ${
                                isActive ? "status-active" : "status-inactive"
                              }`}
                              onClick={() => handleStatusToggle(row)}
                              aria-label={
                                isActive
                                  ? "Deactivate language"
                                  : "Activate language"
                              }
                            >
                              <span
                                className={`status-knob ${
                                  isActive ? "knob-active" : "knob-inactive"
                                }`}
                              />
                            </div>

                            <span
                              className={`status-text ${
                                isActive ? "text-active" : "text-inactive"
                              }`}
                            >
                              {isActive ? "Active" : "Inactive"}
                            </span>
                          </div>
                        </td>

                        {/* DEFAULT LANGUAGE */}

                        <td className="um-td text-center">
                          <div className="form-check d-flex justify-content-center">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="defaultLanguage"
                              checked={Number(row.isdefault) === 1}
                              onChange={() => handleDefaultLanguage(row)}
                              style={{
                                width: "18px",
                                height: "18px",
                                cursor: "pointer",
                              }}
                            />
                          </div>
                        </td>

                        {/* ACTION */}

                        <td className="um-td text-center">
                          <div className="action-buttons">
                            {/* VIEW */}

                            <button
                              type="button"
                              className="action-btn action-view"
                              title="View Language"
                              onClick={() => handleView(row)}
                            >
                              <FiEye size={15} />
                            </button>

                            {/* EDIT */}

                            <button
                              type="button"
                              className="action-btn action-edit"
                              title="Edit Language"
                              onClick={() => startEdit(row)}
                            >
                              <FiEdit size={15} />
                            </button>

                            {/* DELETE */}

                            <button
                              type="button"
                              className="action-btn action-delete delete-enabled"
                              title="Delete Language"
                              onClick={() => setDelModal(row)}
                            >
                              <FiTrash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {/*   PAGINATION */}

            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={10}
              loading={loading}
              onPrevious={handlePrevious}
              onNext={handleNext}
            />
          </div>
        </div>

        {/*  ADD / EDIT LANGUAGE MODAL*/}

        {formModal && (
          <div className="um-overlay" onClick={closeFormModal}>
            <div
              className="um-modal um-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              {/* HEADER */}

              <div className="um-modal-head">
                <div className="d-flex align-items-center gap-2">
                  <div className="form-card-icon">
                    {editId ? <FiEdit size={24} /> : <FiPlus size={24} />}
                  </div>

                  <div>
                    <h2 className="um-modal-title mb-0">
                      {editId ? "Edit Language" : "Add Language"}
                    </h2>
                  </div>
                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={closeFormModal}
                  disabled={saving}
                >
                  ✕
                </button>
              </div>

              {/* BODY */}

              <div className="um-modal-scroll">
                <div className="row g-3">
                  {/* LANGUAGE NAME */}

                  <div className="col-md-12">
                    <label className="field-label">Language Name *</label>

                    <input
                      type="text"
                      className="rd-input"
                      style={{
                        textAlign: "start",
                      }}
                      placeholder="Enter Language Name"
                      maxLength={255}
                      value={form.lang_name}
                      onChange={(e) =>
                        handleChange("lang_name", e.target.value)
                      }
                    />
                  </div>

                  {/* COUNTRY FLAG */}

                  <div className="col-md-12">
                    <label className="field-label">Country Flag</label>

                    <input
                      type="file"
                      name="lang_flag"
                      id="lang_flag"
                      className="form-control"
                      accept="image/*"
                      onChange={(e) =>
                        handleChange("lang_flag", e.target.files?.[0] || null)
                      }
                    />

                    {/* EXISTING FLAG */}

                    {editId && viewModal?.lang_flag && (
                      <div className="mt-2">
                        <small className="text-muted">Current flag:</small>

                        <br />

                        <img
                          src={getFlagUrl(viewModal.lang_flag)}
                          alt="Current flag"
                          style={{
                            width: "50px",
                            height: "35px",
                            objectFit: "cover",
                            marginTop: "5px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* FOOTER */}

              <div className="um-modal-foot">
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={closeFormModal}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner" />
                      Saving...
                    </>
                  ) : editId ? (
                    <>
                      <FiSave />
                      Update Language
                    </>
                  ) : (
                    <>
                      <FiPlus />
                      Add Language
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/*  VIEW LANGUAGE MODAL*/}

        {viewModal && (
          <div className="um-overlay" onClick={() => setViewModal(null)}>
            <div
              className="um-modal um-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              {/* HEADER */}

              <div className="um-modal-head">
                <div>
                  <h2 className="um-modal-title mb-0">
                    <FiEye /> Language Details
                  </h2>

                  <p className="form-card-sub mb-0">
                    View language information
                  </p>
                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={() => setViewModal(null)}
                >
                  ✕
                </button>
              </div>

              {/* BODY */}

              <div className="um-modal-scroll">
                <div className="row g-3">
                  {/* LANGUAGE NAME */}

                  <div className="col-md-12">
                    <div className="form-label">Language Name</div>

                    <div className="form-value">
                      <span className="car-name-badge">
                        {viewModal.lang_name || "—"}
                      </span>
                    </div>
                  </div>

                  {/* FLAG */}

                  <div className="col-md-12">
                    <div className="form-label">Country Flag</div>

                    <div className="form-value">
                      {viewModal.lang_flag ? (
                        <img
                          src={getFlagUrl(viewModal.lang_flag)}
                          alt={viewModal.lang_name || "Language flag"}
                          crossOrigin="anonymous"
                          style={{
                            width: "80px",
                            height: "55px",
                            objectFit: "cover",
                            borderRadius: "5px",
                            border: "1px solid #ddd",
                          }}
                        />
                      ) : (
                        <span>—</span>
                      )}
                    </div>
                  </div>

                  {/* STATUS */}

                  <div className="col-md-6">
                    <div className="form-label">Status</div>

                    <div className="form-value">
                      <span
                        className={
                          Number(viewModal.lang_active) === 1
                            ? "viewnameChipAct"
                            : "viewnameChipInAct"
                        }
                      >
                        {Number(viewModal.lang_active) === 1
                          ? "Active"
                          : "Inactive"}
                      </span>
                    </div>
                  </div>

                  {/* DEFAULT */}

                  <div className="col-md-6">
                    <div className="form-label">Default Language</div>

                    <div className="form-value">
                      <span>
                        {Number(viewModal.isdefault) === 1 ? "Yes" : "No"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* FOOTER */}

              <div className="um-modal-foot">
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={() => setViewModal(null)}
                >
                  Close
                </button>

                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={() => {
                    const selected = viewModal;

                    setViewModal(null);

                    setTimeout(() => {
                      startEdit(selected);
                    }, 100);
                  }}
                >
                  <FiEdit />
                  Edit Language
                </button>
              </div>
            </div>
          </div>
        )}

        {/*  DELETE LANGUAGE MODAL*/}

        {delModal && (
          <div className="um-overlay" onClick={() => setDelModal(null)}>
            <div
              className="um-modal um-delete-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="delete-content">
                {/* ICON */}

                <div className="delete-icon">
                  <FiTrash2 size={32} />
                </div>

                {/* TITLE */}

                <h2 className="delete-title">Delete Language?</h2>

                {/* DESCRIPTION */}

                <p className="delete-description">
                  Are you sure you want to delete{" "}
                  <span className="badge badge-success">
                    "{delModal.lang_name || "this language"}"
                  </span>
                  ?
                  <br />
                  This will permanently remove this language.
                  <br />
                  <strong>This action cannot be undone.</strong>
                </p>

                {/* ACTIONS */}

                <div className="delete-actions">
                  <button
                    type="button"
                    className="um-btn-secondary"
                    onClick={() => setDelModal(null)}
                    disabled={delSaving}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    disabled={delSaving}
                    className="delete-confirm-btn"
                    onClick={handleDelete}
                  >
                    {delSaving ? (
                      <>
                        <span className="spinner" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <FiTrash2 size={16} />
                        Delete Permanently
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </Layout>
  );
}
